// src/types/taskManager.ts

// ============================================================
// CORE MODELS
// ============================================================

export interface Order {
  id: number;
  order_id: string;
  order_key: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'partial';
  user?: number;
  owner: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  updated_at: string;
  options: Record<string, any>;
  result?: Record<string, any>;
  error_message?: string;
  retry_count: number;
  max_retries: number;
  axon_status?: string;
  axon_metadata?: Record<string, any>;
  scheduled_task?: number;
  tasks?: Task[];
  duration?: number;
  is_complete: boolean;
  can_retry: boolean;
}

export interface Task {
  id: number;
  task_id: string;
  order: number;
  task_key: string;
  task_type: string;
  task_order: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  options: Record<string, any>;
  result?: Record<string, any>;
  error_message?: string;
  retry_count: number;
  max_retries: number;
  duration?: number;
  is_complete: boolean;
}

export interface JobExecution {
  id: number;
  job_id: string;
  job_type: string;
  payload: Record<string, any>;
  status: ExecutionStatus;
  started_at?: string;
  completed_at?: string;
  result?: Record<string, any>;
  error_message?: string;
  created_at: string;
  updated_at: string;
  retry_count: number;
  tasks?: TaskExecution[];
}

export interface TaskExecution {
  id: number;
  job: number;
  task_id: string;
  task_type: string;
  task_order: number;
  payload: Record<string, any>;
  status: ExecutionStatus;
  started_at?: string;
  completed_at?: string;
  result?: Record<string, any>;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface ScheduledTask {
  id: number;
  name: string;
  description?: string;
  job_type: string;
  job_payload: Record<string, any>;
  interval_type: IntervalType;
  interval_value: number;
  cron_expression?: string;
  start_time: string;
  end_time?: string;
  timezone_name: string;
  last_run?: string;
  next_run: string;
  run_count: number;
  failure_count: number;
  status: TaskStatus;
  enabled: boolean;
  max_failures: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  tags: string[];
  recent_executions?: ScheduledTaskExecution[];
  is_due_now: boolean;
}

export interface ScheduledTaskExecution {
  id: number;
  scheduled_task: number;
  scheduled_task_name: string;
  job_id: string;
  scheduled_time: string;
  started_at: string;
  completed_at?: string;
  status: ExecutionStatus;
  order?: number;
  job_execution_id?: number;
  result?: Record<string, any>;
  error_message?: string;
  execution_duration?: string;
}

// ============================================================
// PAYLOAD & DEFINITION TYPES
// ============================================================


export interface TaskDefinition {
  task_id: string;
  task_type: TaskType;
  [key: string]: any; // Additional task-specific fields
}


// ============================================================
// ENUMS & TYPE ALIASES
// ============================================================

export type IntervalType =
  | 'once'
  | 'minutely'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'cron';

export type TaskStatus =
  | 'active'
  | 'paused'
  | 'expired'
  | 'disabled';

export type ExecutionStatus =
  | 'pending'
  | 'submitted'
  | 'running'
  | 'completed'
  | 'failed'
  | 'skipped';

export type TaskType =
  | 'approval_notification'
  | 'database_update'
  | 'api_call'
  | 'webex_notification'
  | 'sample';

export type NotificationType =
  | 'summary'
  | 'individual';

// ============================================================
// CONSTANTS & OPTIONS
// ============================================================

export const INTERVAL_TYPES: Array<{ value: IntervalType; label: string }> = [
  { value: 'once', label: 'One Time' },
  { value: 'minutely', label: 'Every Minute(s)' },
  { value: 'hourly', label: 'Every Hour(s)' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'cron', label: 'Custom Cron' },
];

export const TASK_STATUS_OPTIONS: Array<{
  value: TaskStatus;
  label: string;
  color: string
}> = [
  { value: 'active', label: 'Active', color: '#4caf50' },
  { value: 'paused', label: 'Paused', color: '#ff9800' },
  { value: 'expired', label: 'Expired', color: '#9e9e9e' },
  { value: 'disabled', label: 'Disabled', color: '#f44336' },
];

export const EXECUTION_STATUS_OPTIONS: Array<{
  value: ExecutionStatus;
  label: string;
  color: string
}> = [
  { value: 'pending', label: 'Pending', color: '#2196f3' },
  { value: 'submitted', label: 'Submitted', color: '#00bcd4' },
  { value: 'running', label: 'Running', color: '#ff9800' },
  { value: 'completed', label: 'Completed', color: '#4caf50' },
  { value: 'failed', label: 'Failed', color: '#f44336' },
  { value: 'skipped', label: 'Skipped', color: '#9e9e9e' },
];

export const TASK_TYPES: Array<{
  value: TaskType;
  label: string;
  description: string
}> = [
  {
    value: 'approval_notification',
    label: 'Approval Notification',
    description: 'Send Webex notifications for pending approvals'
  },
  {
    value: 'webex_notification',
    label: 'Webex Notification',
    description: 'Send generic Webex messages or cards'
  },
  {
    value: 'database_update',
    label: 'Database Update',
    description: 'Perform database operations'
  },
  {
    value: 'api_call',
    label: 'API Call',
    description: 'Call external APIs'
  },
  {
    value: 'sample',
    label: 'Sample Task',
    description: 'Test task for development'
  },
];

export const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
];

export const NOTIFICATION_TYPE_OPTIONS: Array<{
  value: NotificationType;
  label: string;
  description: string;
}> = [
  {
    value: 'summary',
    label: 'Summary (Grouped by Approver)',
    description: 'One notification per approver with all their pending approvals'
  },
  {
    value: 'individual',
    label: 'Individual (One per Approval)',
    description: 'Separate notification for each approval request'
  },
];

// ============================================================
// TASK TEMPLATES
// ============================================================

export const TASK_TEMPLATES: Record<string, Partial<ScheduledTask>> = {
  approval_notification: {
    name: 'Daily Approval Reminders',
    description: 'Send daily reminders for pending approvals via Webex',
    job_type: 'approval_notification',
    job_payload: {
      tasks: [
        {
          task_id: 'send_approval_reminders',
          task_type: 'approval_notification',
          notification_type: 'summary',
          min_age_days: 0,
          base_url: typeof window !== 'undefined' ? window.location.origin : '',
        },
      ],
      metadata: {
        description: 'Daily approval reminder notifications',
      },
    },
    interval_type: 'daily',
    interval_value: 1,
    timezone_name: 'America/Los_Angeles',
    tags: ['approvals', 'notifications', 'webex'],
    max_failures: 3,
  },

  weekly_approval_summary: {
    name: 'Weekly Approval Summary',
    description: 'Send weekly summary of all pending approvals',
    job_type: 'approval_notification',
    job_payload: {
      tasks: [
        {
          task_id: 'weekly_summary',
          task_type: 'approval_notification',
          notification_type: 'summary',
          min_age_days: 0,
          base_url: typeof window !== 'undefined' ? window.location.origin : '',
        },
      ],
    },
    interval_type: 'weekly',
    interval_value: 1,
    timezone_name: 'America/Los_Angeles',
    tags: ['approvals', 'weekly', 'summary'],
    max_failures: 3,
  },

  old_approval_alert: {
    name: 'Old Approval Alerts',
    description: 'Alert for approvals pending more than 7 days',
    job_type: 'approval_notification',
    job_payload: {
      tasks: [
        {
          task_id: 'old_approval_alert',
          task_type: 'approval_notification',
          notification_type: 'individual',
          min_age_days: 7,
          base_url: typeof window !== 'undefined' ? window.location.origin : '',
        },
      ],
    },
    interval_type: 'daily',
    interval_value: 1,
    timezone_name: 'America/Los_Angeles',
    tags: ['approvals', 'alerts', 'overdue'],
    max_failures: 3,
  },

  custom: {
    name: 'Custom Task',
    description: '',
    job_type: '',
    job_payload: {
      tasks: [],
    },
    interval_type: 'daily',
    interval_value: 1,
    timezone_name: 'UTC',
    tags: [],
    max_failures: 3,
  },
};